// ─── WeeklyPulse ─────────────────────────────────────────────────────────
// 2026-07-22 — « Le point de la semaine » sur le Dashboard d'Anissa.
// Décision Benoit : le récap du lundi vit dans le SaaS, pas par email
// (le cron monday-digest côté app a été retiré le même jour).
//
// Un encart compact en tête de Dashboard : à traiter dans le flux,
// silences, RDV des 7 prochains jours. Tout clic ouvre le Suivi — l'encart
// ORIENTE, il ne duplique pas le flux (mêmes règles de calcul que
// SuiviCockpit : last_reviewed_at serveur, silences = programme actif non
// oneshot sans ressenti depuis 7 jours).
//
// Discret par nature : rien pendant le chargement, rien si l'API échoue
// (le Suivi reste la source de vérité, l'encart est un plus).

import { useEffect, useState } from "react";
import { fetchFeedbacksStream } from "./services/fetchFeedbacksStream";

const SILENCE_DAYS = 7;
const WEEK_AHEAD_MS = 7 * 86400000;

export default function WeeklyPulse({ onOpenSuivi }) {
  const [pulse, setPulse] = useState(null);

  useEffect(() => {
    let dead = false;
    fetchFeedbacksStream(30)
      .then((s) => {
        if (dead) return;
        const now = Date.now();
        const reviewedAt = (c) => (c.last_reviewed_at ? new Date(c.last_reviewed_at).getTime() : 0);

        const lastFb = new Map();
        for (const f of s.feedbacks) {
          const t = new Date(f.created_at).getTime();
          if (t > (lastFb.get(f.client_id) || 0)) lastFb.set(f.client_id, t);
        }
        const silences = s.clients
          .filter((c) => c.mode !== "oneshot" && c.journey_status === "program_active")
          .map((c) => ({ name: c.first_name || "Cliente", last: lastFb.get(c.client_id) || 0 }))
          .filter(({ last }) => !last || (now - last) / 86400000 >= SILENCE_DAYS);

        const byId = new Map(s.clients.map((c) => [c.client_id, c]));
        const items = [
          ...s.feedbacks.map((f) => ({ cid: f.client_id, at: f.created_at })),
          ...(s.photos || []).map((p) => ({ cid: p.client_id, at: p.created_at })),
          ...(s.clientMessages || []).map((m) => ({ cid: m.client_id, at: m.sent_at })),
        ];
        const aTraiter = items.filter((i) => {
          const c = byId.get(i.cid);
          return c && new Date(i.at).getTime() > reviewedAt(c);
        }).length;

        const rdv = s.clients.filter((c) => {
          if (!c.rdv_scheduled_at) return false;
          const t = new Date(c.rdv_scheduled_at).getTime();
          return Number.isFinite(t) && t > now - 12 * 3600000 && t < now + WEEK_AHEAD_MS;
        }).length;

        setPulse({ silences, aTraiter, rdv });
      })
      .catch(() => {
        if (!dead) setPulse(null);
      });
    return () => {
      dead = true;
    };
  }, []);

  if (!pulse) return null;

  const { silences, aTraiter, rdv } = pulse;
  const calm = aTraiter === 0 && silences.length === 0;
  const silenceNames = silences.slice(0, 3).map((s) => s.name).join(", ");

  return (
    <button type="button" className="weekly-pulse" onClick={onOpenSuivi} title="Ouvrir le Suivi">
      <div className="weekly-pulse__content">
        <div className="weekly-pulse__title">Le point de la semaine</div>
        <div className="weekly-pulse__signals">
          <Seg tone={aTraiter > 0 ? "warm" : "ok"}>
            {aTraiter > 0 ? `${aTraiter} à traiter` : "Flux à jour"}
          </Seg>
          <Seg tone={silences.length > 0 ? "warm" : "ok"}>
            {silences.length > 0
              ? `${silences.length} silence${silences.length > 1 ? "s" : ""}${silenceNames ? ` · ${silenceNames}${silences.length > 3 ? "…" : ""}` : ""}`
              : "Aucun silence"}
          </Seg>
          <Seg tone="neutral">
            {rdv > 0 ? `${rdv} RDV cette semaine` : "Pas de RDV posé"}
          </Seg>
        </div>
        {calm && <div className="weekly-pulse__calm">Tout est à jour — belle semaine.</div>}
      </div>
      <span className="weekly-pulse__cta">Ouvrir le Suivi <span aria-hidden="true">→</span></span>
    </button>
  );
}

function Seg({ tone, children }) {
  const tones = {
    ok: { color: "#2f6a43", background: "rgba(72,130,88,.12)" },
    warm: { color: "#8c5a16", background: "rgba(184,131,50,.14)" },
    neutral: { color: "#5f6b62", background: "rgba(36,59,42,.07)" },
  };
  return (
    <span
      className="weekly-pulse__signal"
      style={{ ...(tones[tone] || tones.neutral) }}
    >
      {children}
    </span>
  );
}
